import React from 'react'
import { useTranslation } from 'react-i18next'
import uniq from 'lodash/fp/uniq'
import { IGenericVehicle } from '@cbgms/api'
import { Grid, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField } from '@mui/material'
import sortBy from 'lodash/sortBy'
import reverse from 'lodash/reverse'
import filter from 'lodash/filter'
import Search from '@mui/icons-material/Search'
import { useEnumTranslation } from '@cbgms/base/enums/use-enum-translation'
import { EmptyDate } from '@cbgms/base/utils/datetime'
export interface IVehicleRefinementTableProps {
  vehicles: IGenericVehicle[]
  onSelect?: (_: IGenericVehicle) => void
  hideVin?: boolean
}

export interface ITableHeaderWithSortingProps {
  setSort: Function
  sorting: ISortSetings
  title: string
  field: string
}

type ISortDirecrion = 'asc' | 'desc' | ''
export interface ISortSetings {
  field: string
  direction: ISortDirecrion
}

function getNextDirection(direction: ISortDirecrion): ISortDirecrion {
  if (direction === '') return 'asc'
  if (direction === 'asc') return 'desc'
  return ''
}

const TableHeaderWithSorting = (props: ITableHeaderWithSortingProps) => {
  const active = !!(props.field === props.sorting.field && props.sorting.direction)
  return (
    <TableCell>
      <TableSortLabel
        active={active}
        direction={props.sorting.direction === '' ? undefined : props.sorting.direction}
        onClick={() => props.setSort(props.field)}
      >
        {props.title}
      </TableSortLabel>
    </TableCell>
  )
}

const VehicleRefinementTable: React.FC<IVehicleRefinementTableProps> = ({ vehicles, onSelect, hideVin }) => {
  const { t } = useTranslation()
  const { tes } = useEnumTranslation()
  const [sorting, setSorting] = React.useState<ISortSetings>({ field: '', direction: '' })
  const [filtering, setFiltering] = React.useState<string>('')

  // In HBase Remarks = enginecodes (+ sometimes powerkw), other providers don't have remarks on types
  const showRemarks = React.useMemo(() => vehicles.some(v => v.Remarks), [vehicles])
  const showVin = React.useMemo(() => !hideVin && vehicles.some(v => v.VIN), [vehicles, hideVin])
  const showPowerKw = React.useMemo(() => vehicles.some(v => v.PowerKw > 0), [vehicles])
  // For enginecodes we get the length after filtering, because some come back with { EngineCodes: [""] }, which isn't an empty array.
  const showEngineCodes = React.useMemo(() => vehicles.some(v => v.EngineCodes?.filter(a => a).length), [vehicles])
  const showFoundOnArray = React.useMemo(() => vehicles.some(v => v.FoundOn?.length), [vehicles])
  const showType = React.useMemo(() => vehicles.some(v => v.Type), [vehicles])
  const showFuel = React.useMemo(() => vehicles.some(v => v.FuelTypes?.length), [vehicles])
  const showConstructionYear = React.useMemo(() => vehicles.some(v => v.MadeUntil || v.MadeFrom), [vehicles])
  const multipleSources = React.useMemo(() => uniq(vehicles.map(({ Source }) => Source)).length > 1, [vehicles])

  const handleSortingChange = (field: string) => {
    const nextDirection: ISortDirecrion = sorting.field === field ? getNextDirection(sorting.direction) : 'asc'
    setSorting({ field: nextDirection === '' ? '' : field, direction: nextDirection })
  }

  const filterAndSortVehicles = () => {
    return sortBy(
      filter(vehicles, vehicle => {
        return Object.values(vehicle).some(value => {
          return value.toString().toLowerCase().includes(filtering.toLowerCase())
        })
      }),
      sorting.field
    )
  }

  const vehiclesToDisplay = sorting.direction === 'desc' ? reverse(filterAndSortVehicles()) : filterAndSortVehicles()

  return (
    <span>
      <Grid container spacing={1} alignItems='flex-end'>
        <Grid item xs={12}>
          <TextField
            id='standard-required'
            fullWidth
            label={t('global.search_vehicle_placeholder')}
            onChange={event => setFiltering(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              )
            }}
          />
        </Grid>
      </Grid>
      <Table size='small'>
        <TableHead>
          <TableRow>
            {multipleSources && (
              <TableHeaderWithSorting field='Source' sorting={sorting} setSort={handleSortingChange} title={t('vehicle.source')} />
            )}
            {showFoundOnArray && (
              <TableHeaderWithSorting field='FoundOn' sorting={sorting} setSort={handleSortingChange} title={t('vehicle.found-on')} />
            )}
            <TableHeaderWithSorting field='Manufacturer' sorting={sorting} setSort={handleSortingChange} title={t('vehicle.brand')} />
            <TableHeaderWithSorting field='Model' sorting={sorting} setSort={handleSortingChange} title={t('vehicle.model')} />
            {showType && <TableHeaderWithSorting field='Type' sorting={sorting} setSort={handleSortingChange} title={t('vehicle.type')} />}
            {showRemarks && (
              <TableHeaderWithSorting field='Remarks' sorting={sorting} setSort={handleSortingChange} title={t('vehicle.remarks')} />
            )}
            {showEngineCodes && (
              <TableHeaderWithSorting
                field='EngineCodes'
                sorting={sorting}
                setSort={handleSortingChange}
                title={t('vehicle.engine_code')}
              />
            )}
            {showPowerKw && (
              <TableHeaderWithSorting
                field='PowerKw'
                sorting={sorting}
                setSort={handleSortingChange}
                title={t('vehicle.engine_performance')}
              />
            )}
            {showFuel && (
              <TableHeaderWithSorting field='FuelTypes' sorting={sorting} setSort={handleSortingChange} title={t('vehicle.fuel')} />
            )}
            {showConstructionYear && (
              <TableHeaderWithSorting
                field='MadeFrom'
                sorting={sorting}
                setSort={handleSortingChange}
                title={t('vehicle.construction_year')}
              />
            )}
            {showVin && <TableHeaderWithSorting field='VIN' sorting={sorting} setSort={handleSortingChange} title={t('vehicle.vin')} />}
          </TableRow>
        </TableHead>

        <TableBody data-testid='vehicle-suggestion-table'>
          {vehiclesToDisplay.map((vehicle, key) => (
            <TableRow data-testid={`carsToRefine${key}`} key={key} onClick={() => onSelect?.(vehicle)} className='clickable'>
              {multipleSources && <TableCell>{vehicle.Source}</TableCell>}
              {showFoundOnArray && (
                <TableCell>
                  {vehicle.FoundOn?.map(value => t(`vehicle.found-on_${value}`))
                    .sort()
                    .join(', ')}
                </TableCell>
              )}
              <TableCell>{vehicle.Manufacturer}</TableCell>
              <TableCell>{vehicle.Model}</TableCell>
              {showType && <TableCell>{vehicle.Type}</TableCell>}
              {showRemarks && <TableCell>{vehicle.Remarks}</TableCell>}
              {showEngineCodes && <TableCell>{vehicle.EngineCodes && vehicle.EngineCodes.join(', ')}</TableCell>}
              {showPowerKw && (
                <TableCell>
                  {vehicle.PowerKw} {t('vehicle.engine_performance_unit_kw')}
                </TableCell>
              )}
              {showFuel && <TableCell>{vehicle.FuelTypes && tes('EnumFuelType', vehicle.FuelTypes)}</TableCell>}
              {showConstructionYear && (
                <TableCell>
                  {vehicle.MadeFrom}
                  &nbsp;
                  {t('vehicle.until').toLowerCase()}
                  &nbsp;
                  {vehicle.MadeUntil === EmptyDate ? '-' : vehicle.MadeUntil}
                </TableCell>
              )}
              {showVin && <TableCell>{vehicle.VIN}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </span>
  )
}

export default VehicleRefinementTable
